import React from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'

const businessTypes = [
  { value: 'ice_rink', label: 'Ice Rink / Hockey Facility' },
  { value: 'training_center', label: 'Training Center / Gym' },
  { value: 'sports_complex', label: 'Sports Complex' },
  { value: 'dance_martial_arts', label: 'Dance / Martial Arts Studio' },
  { value: 'other', label: 'Other' }
]

const timezones = [
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' }
]

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export default function OrganizationStep() {
  const { wizardData, updateStepData, getStepErrors } = useSetupWizard()
  const colors = useThemeColors()
  const { organizationProfile } = wizardData
  const errors = getStepErrors(1)

  const updateProfile = (field: string, value: string | number | boolean) => {
    updateStepData('organizationProfile', { [field]: value })
  }

  const updateBusinessHours = (day: string, field: string, value: string | boolean) => {
    const newHours = {
      ...organizationProfile.businessHours,
      [day]: {
        ...organizationProfile.businessHours[day],
        [field]: value
      }
    }
    updateProfile('businessHours', newHours)
  }

  return (
    <div className="organization-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Tell us about your business
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        This information will be used for your invoices, member communications, and public profile.
      </p>

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: `${colors.error}20`,
          border: `1px solid ${colors.error}40`,
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: colors.error,
            margin: 0,
            fontWeight: '500'
          }}>
            Please complete the following:
          </p>
          <ul style={{
            margin: '0.5rem 0 0 1.5rem',
            padding: 0,
            fontSize: '0.875rem',
            color: colors.error
          }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{
        display: 'grid',
        gap: '1.5rem'
      }}>
        {/* Business Name */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '0.5rem',
            color: colors.text
          }}>
            Business Name <span style={{ color: colors.error }}>*</span>
          </label>
          <input
            type="text"
            value={organizationProfile.businessName}
            onChange={(e) => updateProfile('businessName', e.target.value)}
            placeholder="e.g., The Hockey Factory"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '0.875rem'
            }}
          />
        </div>

        {/* Business Type */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '0.5rem',
            color: colors.text
          }}>
            Business Type <span style={{ color: colors.error }}>*</span>
          </label>
          <select
            value={organizationProfile.businessType}
            onChange={(e) => updateProfile('businessType', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            {businessTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {organizationProfile.businessType === 'other' && (
            <input
              type="text"
              value={organizationProfile.businessTypeOther || ''}
              onChange={(e) => updateProfile('businessTypeOther', e.target.value)}
              placeholder="Please specify"
              style={{
                width: '100%',
                padding: '0.75rem',
                marginTop: '0.5rem',
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.875rem'
              }}
            />
          )}
        </div>

        {/* Contact Information */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem'
        }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '0.5rem',
              color: colors.text
            }}>
              Phone Number <span style={{ color: colors.error }}>*</span>
            </label>
            <input
              type="tel"
              value={organizationProfile.phone}
              onChange={(e) => updateProfile('phone', e.target.value)}
              placeholder="(555) 123-4567"
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.875rem'
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '0.5rem',
              color: colors.text
            }}>
              Email Address <span style={{ color: colors.error }}>*</span>
            </label>
            <input
              type="email"
              value={organizationProfile.email}
              onChange={(e) => updateProfile('email', e.target.value)}
              placeholder="info@yourbusiness.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '0.5rem',
            color: colors.text
          }}>
            Business Address <span style={{ color: colors.error }}>*</span>
          </label>
          <input
            type="text"
            value={organizationProfile.address}
            onChange={(e) => updateProfile('address', e.target.value)}
            placeholder="123 Main Street"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '0.875rem',
              marginBottom: '0.5rem'
            }}
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: '0.5rem'
          }}>
            <input
              type="text"
              value={organizationProfile.city}
              onChange={(e) => updateProfile('city', e.target.value)}
              placeholder="City"
              style={{
                padding: '0.75rem',
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.875rem'
              }}
            />
            <input
              type="text"
              value={organizationProfile.state}
              onChange={(e) => updateProfile('state', e.target.value)}
              placeholder="State"
              style={{
                padding: '0.75rem',
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.875rem'
              }}
            />
            <input
              type="text"
              value={organizationProfile.zipCode}
              onChange={(e) => updateProfile('zipCode', e.target.value)}
              placeholder="ZIP"
              style={{
                padding: '0.75rem',
                backgroundColor: colors.background,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.875rem'
              }}
            />
          </div>
        </div>

        {/* Timezone */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '0.5rem',
            color: colors.text
          }}>
            Timezone
          </label>
          <select
            value={organizationProfile.timezone}
            onChange={(e) => updateProfile('timezone', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        {/* Business Hours */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '0.5rem',
            color: colors.text
          }}>
            Business Hours
          </label>
          <div style={{
            backgroundColor: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '1rem'
          }}>
            {days.map(day => {
              const hours = organizationProfile.businessHours[day]
              return (
                <div key={day} style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr',
                  gap: '1rem',
                  alignItems: 'center',
                  marginBottom: day === 'sunday' ? 0 : '0.75rem'
                }}>
                  <label style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    textTransform: 'capitalize',
                    color: hours.closed ? colors.textMuted : colors.text
                  }}>
                    {day}
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center'
                  }}>
                    <input
                      type="checkbox"
                      checked={!hours.closed}
                      onChange={(e) => updateBusinessHours(day, 'closed', !e.target.checked)}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                    />
                    {!hours.closed && (
                      <>
                        <input
                          type="time"
                          value={hours.open}
                          onChange={(e) => updateBusinessHours(day, 'open', e.target.value)}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.813rem'
                          }}
                        />
                        <span style={{ color: colors.textSecondary }}>to</span>
                        <input
                          type="time"
                          value={hours.close}
                          onChange={(e) => updateBusinessHours(day, 'close', e.target.value)}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: colors.backgroundLight,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.813rem'
                          }}
                        />
                      </>
                    )}
                    {hours.closed && (
                      <span style={{
                        fontSize: '0.813rem',
                        color: colors.textMuted
                      }}>
                        Closed
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}